import { useCallback, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { getPropertyValueMinusMortgage } from 'utils/advisoty';
import { STATUS_OK } from 'constants/constants';
import { AdvisoryRealEstateMetadataPropertyResponse } from 'core/types/api/Api';
import { usePortfolioProcessNavigation } from 'hooks/helpers/usePortfolioProcessNavigation';
import { useCreatePortfolioActions, useCreatePortfolioSelectors } from 'datasource/CreatePortfolio';
import { TPageError } from 'datasource/PortfolioProcessLayout/PortfolioFlowLayoutManager.types';

export const useRealEstateData = () => {
    const { advisoryIdSelector, advisoryDataSelector, advisoryOptions } = useCreatePortfolioSelectors();
    const { saveAdvisoryDataAction } = useCreatePortfolioActions();
    const [continueButtonLoading, setContinueButtonLoading] = useState(false);
    const [pageErrors, setPageErrors] = useState<TPageError | undefined>(undefined);

    const realStateData = useMemo(() => advisoryDataSelector?.metadata?.realEstate?.properties || [], [advisoryDataSelector?.metadata?.realEstate?.properties]);

    const {
        control, handleSubmit, formState: { errors, isDirty }, watch,
    } = useForm({
        defaultValues: {
            realEstate: realStateData?.map(dta => ({
                propertyListItemId: dta?.propertyListItemId,
                value: dta?.value?.toFixed(2),
                mortgage: dta?.mortgage?.toFixed(2),
            })),
        },
    });

    const {
        fields,
        append,
        remove,
    } = useFieldArray({
        control,
        name: 'realEstate',
    });

    const formData = watch();

    const handleAddProperty = useCallback(() => {
        append({
            propertyListItemId: undefined,
            value: undefined,
            mortgage: undefined,
        });
    }, [append]);

    const handleRemoveProperty = useCallback((index: number) => {
        remove(index);
    }, [remove]);

    const advisoryRealEstatePropertyTypes = advisoryOptions?.realEstateProperties || {};

    const getTotalPropertyValue = useCallback(() => getPropertyValueMinusMortgage(formData.realEstate || null), [formData?.realEstate]);

    const saveData = async (data: AdvisoryRealEstateMetadataPropertyResponse[]) => {
        const payload = {
            properties: [...data],
        };

        if (advisoryIdSelector) {
            const result:any = await saveAdvisoryDataAction({
                key: 'real-estate',
                payload,
                setPageErrors,
            });

            if (result?.status === STATUS_OK) {
                unlockNavigationAction();
                stepComplete();
            }
            setContinueButtonLoading(false);
        }
    };

    const onSubmit = useCallback(async (dta) => {
        if (isDirty) {
            setContinueButtonLoading(true);

            await saveData(dta?.realEstate?.map(type => ({
                propertyListItemId: type?.propertyListItemId,
                value: +type?.value,
                mortgage: +type?.mortgage,
            })));
        } else {
            setContinueButtonLoading(false);
            unlockNavigationAction();
            stepComplete();
        }
    }, [isDirty]);

    const { stepComplete, unlockNavigationAction } = usePortfolioProcessNavigation({
        saveOnContinue: useCallback(handleSubmit(onSubmit), [onSubmit]),
        continueInProgress: continueButtonLoading,
        enableAutoStepComplete: !isDirty,
        pageErrors,
    });

    return {
        advisoryRealEstatePropertyTypes,
        fields,
        errors,
        control,
        handleAddProperty,
        handleRemoveProperty,
        getPropertyValueMinusMortgage: getTotalPropertyValue,
    };
};
